import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Camera, Upload, X, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";

export default function SpotlightPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [uploading, setUploading] = useState(false);
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    user_name: "",
    dish_name: "",
    description: "",
    image: null
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/spotlight?limit=30`);
      setPosts(response.data);
    } catch (error) {
      console.error("Failed to fetch spotlight posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.image) {
      toast.error("Please select an image");
      return;
    }

    setUploading(true);
    try {
      const data = new FormData();
      data.append("user_name", formData.user_name);
      data.append("dish_name", formData.dish_name);
      data.append("description", formData.description);
      data.append("image", formData.image);
      if (user?.avatar) {
        data.append("user_avatar", user.avatar);
      }

      await axios.post(`${API}/spotlight`, data, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      toast.success("Your dish has been submitted for review!");
      setShowUploadForm(false);
      setFormData({ user_name: "", dish_name: "", description: "", image: null });
      fetchPosts();
    } catch (error) {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const displayPosts = posts;

  return (
    <div className="pt-16 md:pt-20 lg:pt-24" data-testid="spotlight-page">
      <SEO title="Customer Spotlight" description="See amazing dishes created by our community using Praroha microgreens. Share your own creations!" keywords="microgreens recipes, customer dishes, community spotlight" path="/spotlight" />
      {/* Hero */}
      <section className="py-8 md:py-10 bg-praroha-forest">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Camera className="w-5 h-5 text-praroha-sage" />
                <span className="text-praroha-sage text-sm font-medium">Community</span>
              </div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-white">
                Customer Spotlight
              </h1>
              <p className="text-white/50 text-sm mt-1">
                See the amazing dishes our community creates with Praroha microgreens.
              </p>
            </div>
            <Button
              onClick={() => {
                if (user) setFormData(prev => ({ ...prev, user_name: prev.user_name || user.name || "" }));
                setShowUploadForm(true);
              }}
              className="bg-praroha-terracotta hover:bg-praroha-terracotta/90 text-white rounded-full px-6 py-5 w-fit"
              data-testid="share-dish-btn"
            >
              <Camera className="mr-2 w-4 h-4" />
              Share Your Dish
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-8 md:py-10 bg-background" data-testid="spotlight-gallery">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="aspect-square bg-muted animate-pulse rounded-xl" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {displayPosts.map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="group relative overflow-hidden rounded-xl cursor-pointer"
                  onClick={() => setSelectedPost(post)}
                  data-testid={`spotlight-item-${index}`}
                >
                  <img
                    src={post.image}
                    alt={post.dish_name}
                    className="w-full h-auto object-contain rounded-xl group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <p className="text-white font-medium text-sm">{post.dish_name}</p>
                      <p className="text-white/70 text-xs">by {post.user_name}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Upload Form Dialog */}
      <Dialog open={showUploadForm} onOpenChange={setShowUploadForm}>
        <DialogContent className="sm:max-w-md" data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-praroha-forest">
              Share Your Dish
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {user?.avatar && (
              <div className="flex items-center gap-3 p-3 bg-praroha-cream/40 rounded-xl" data-testid="spotlight-avatar-preview">
                <img src={user.avatar.startsWith("/api") ? `${API.replace('/api','')}${user.avatar}` : user.avatar} alt="Your photo" className="w-10 h-10 rounded-full object-cover" />
                <p className="text-sm text-muted-foreground">Your profile photo will be shown with your post</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="user_name">Your Name</Label>
              <Input
                id="user_name"
                value={formData.user_name}
                onChange={(e) => setFormData({ ...formData, user_name: e.target.value })}
                placeholder="Enter your name"
                required
                data-testid="spotlight-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dish_name">Dish Name</Label>
              <Input
                id="dish_name"
                value={formData.dish_name}
                onChange={(e) => setFormData({ ...formData, dish_name: e.target.value })}
                placeholder="What did you make?"
                required
                data-testid="spotlight-dish-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Tell us about your dish and which microgreens you used..."
                rows={3}
                data-testid="spotlight-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Photo</Label>
              <div className="border-2 border-dashed border-border rounded-xl p-6 text-center">
                {formData.image ? (
                  <div className="relative">
                    <img
                      src={URL.createObjectURL(formData.image)}
                      alt="Preview"
                      className="max-h-40 mx-auto rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, image: null })}
                      className="absolute top-0 right-0 w-6 h-6 bg-destructive text-white rounded-full flex items-center justify-center"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <Upload className="w-8 h-8" />
                      <span className="text-sm">Click to upload or drag and drop</span>
                      <span className="text-xs">PNG, JPG up to 5MB</span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files?.[0]) {
                          setFormData({ ...formData, image: e.target.files[0] });
                        }
                      }}
                      data-testid="spotlight-image-input"
                    />
                  </label>
                )}
              </div>
            </div>
            <Button
              type="submit"
              disabled={uploading}
              className="w-full bg-praroha-forest hover:bg-praroha-forest/90 text-white rounded-full"
              data-testid="spotlight-submit-btn"
            >
              {uploading ? "Submitting..." : "Submit for Review"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Post Detail Dialog */}
      <Dialog open={!!selectedPost} onOpenChange={() => setSelectedPost(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="post-detail-dialog">
          {selectedPost && (
            <>
              <img
                src={selectedPost.image}
                alt={selectedPost.dish_name}
                className="w-full h-auto object-contain rounded-lg"
              />
              <div className="mt-4">
                <div className="flex items-center gap-3">
                  {selectedPost.user_avatar ? (
                    <img src={selectedPost.user_avatar.startsWith("/api") ? `${API.replace('/api','')}${selectedPost.user_avatar}` : selectedPost.user_avatar} alt={selectedPost.user_name} className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-praroha-forest/10 flex items-center justify-center">
                      <span className="font-heading font-bold text-praroha-forest">{selectedPost.user_name?.[0]?.toUpperCase()}</span>
                    </div>
                  )}
                  <div>
                    <h3 className="font-heading text-2xl font-semibold text-praroha-forest">
                      {selectedPost.dish_name}
                    </h3>
                    <p className="text-muted-foreground">by {selectedPost.user_name}</p>
                  </div>
                </div>
                {selectedPost.description && (
                  <p className="text-muted-foreground mt-4">{selectedPost.description}</p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* CTA */}
      <section className="py-10 md:py-12 bg-praroha-forest text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-4">
            Get Featured!
          </h2>
          <p className="text-white/80 mb-8">
            Share your microgreen creations and get featured on our spotlight wall. 
            Use #PrarohaGreens on social media!
          </p>
          <Button
            onClick={() => {
              if (user) setFormData(prev => ({ ...prev, user_name: prev.user_name || user.name || "" }));
              setShowUploadForm(true);
            }}
            className="bg-white text-praroha-sage hover:bg-white/90 rounded-full px-8 py-6 text-lg"
          >
            <ImageIcon className="mr-2 w-5 h-5" />
            Share Your Creation
          </Button>
        </div>
      </section>
    </div>
  );
}
